/** 앱과 웹이 함께 쓰는 돈 문장.
 *
 * 앱과 웹이 같은 돈 문장을 각자 적고 있어 한 곳으로 모았다. 각자 적으면 한쪽만 고쳐지고, 같은
 * 금액을 두 화면이 다른 말로 부르게 된다.
 *
 * 근거: 2026-09-23 사용자 확정(PRD `7.5.9` · `13.1` O-10). 못 만든 장은 다시 만들어도 더 내실
 * 금액이 없고(그 장에 걸린 금액을 그대로 쓴다), 다시 만들지 않으면 그때 돌아간다.
 * 글자의 정본은 목업 `docs/mockup/ver2-ondora-ui.html` 이다. 여기 글자는 목업에서 그대로 떴다.
 * 고칠 때는 목업을 먼저 고친다.
 *
 * ⛔ 30일 같은 기한을 말하는 문장은 일부러 여기 두지 않는다. `docs/LEGAL_IP_GUIDE.md` `6A.12`
 * 의 선행조건이 닫히기 전에는 시점을 약속하지 않는다. 그래서 아래 문장은 모두 조건문이다.
 * 2026-10-04 B안(수동 취소, 사용자 결정) 뒤에 들어온 문장도 같다. 「원본 사진 보관이 끝난 뒤」는
 * 기산점이지 기한이 아니고, 우리 쪽 처리 기한(「며칠」 · 「자동으로」)을 말하지 않는다.
 * 취소는 능동형 「취소합니다」로 쓴다. 지키는 것이 코드가 아니라 사람이라서다(PRD `7.0` 「로그인 전
 * 화면의 확정 문구」 판단 2).
 *
 * D1 에 아무 칸을 두지 않는다. 마이그레이션 0건이다.
 */

import { formatKrw } from "./format";
import { CONTACT_EMAIL } from "./public-copy";

/** A-26 · W-26 결제 확인 배너 첫 문장들. 2026-10-04 「그때」를 걷었다(PRD `7.0` 「로그인 전 화면의
 *  확정 문구」). 돌아가는 때는 환불정책 제4조(`public-copy.ts` `REFUND_RETURN_TIMING`)가 말한다.
 *  ⚠️ 웹 `Pay.tsx` 와 앱 `apps/mobile/src/app/pay/confirm.tsx` 가 이 상수를 읽는다. 웹은 다음 배포에,
 *  **앱은 다음 앱 빌드에서** 새 글자로 바뀐다. 그 전까지 설치된 앱은 옛 글자를 보인다. */
export const PAYMENT_FAILED_IMAGE_NOTICE =
  "결제는 전체 장수로 한 번에 됩니다. 못 만든 장 금액은 다시 만들면 그 장에 그대로 쓰이고, 다시 만들지 않으시면 돌아갑니다.";

/** A-26 · W-26. 결제가 담기를 닫는다는 사실. */
export const UPLOAD_CLOSES_NOTICE =
  "결제하시면 이 작업은 담기가 끝납니다. 더 만드실 사진은 새 작업에 담아 주세요.";

/** A-26 · W-26 · W-26p. 결제했어도 시작 전에 알려 주시면 전액 취소한다는 약속.
 *  2026-10-04 확정(PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 5. B안 사용자 결정). 옛 글자
 *  「시작 전이면 전액 취소됩니다.」는 저절로 취소되는 것으로 읽혀 걷었다. 알리는 길은 환불정책
 *  제5조의 연락처(`public-copy.ts` `CONTACT_EMAIL`)다.
 *  `동의 절차 미정` 배지는 걷는다(같은 절 판단 4. 공개 화면과 결제 화면이 같은 상태로 선다).
 *  웹 `Pay.tsx` 는 2026-10-04 걷었고, 앱 `pay/confirm.tsx` 에만 남아 있어 앱 회차에 걷는다.
 *  배지는 글자가 아니라 표시라 여기 넣지 않는다.
 *  ⚠️ 웹 `Pay.tsx` 와 앱 `apps/mobile/src/app/pay/confirm.tsx` 가 이 상수를 읽는다. 웹은 다음 배포에,
 *  **앱은 다음 앱 빌드에서** 새 글자로 바뀐다. 그 전까지 설치된 앱은 옛 글자를 보인다.
 *  ⛔ 한 줄 문자열 리터럴로 둔다. `scripts/payment-screen.test.mjs` 가 소스에서 이 꼴로 값을 읽는다. */
export const CANCEL_BEFORE_START_NOTICE = "시작 전에 알려 주시면 전액 취소합니다.";

/** 웹 `W-24` 전부 실패 ₩ 배너 둘째 줄(`Making.tsx`)과 청구 내역 아래 안내(`Account.tsx`)가
 *  **함께 쓰는 한 문장**이다(PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 1 · 2, 2026-10-04 확정).
 *  웹은 두 화면이 이 상수를 읽는다. `Making.tsx` 는 진짜 화면 `LiveTotalFailure` 와 예시 `SampleMaking`
 *  두 자리, `Account.tsx` 는 청구 내역 아래 안내의 끝 문장이다.
 *  ⚠️ 앱(`apps/mobile`)의 같은 자리(같은 절 (6))는 아직 읽지 않는다. 실기기 확인이 필요해 앱 회차에
 *  이 상수를 읽게 한다. 각자 적으면 한쪽만 고쳐진다.
 *  B안: 알려 주시면 확인한 뒤 취소하고, 알려 주시지 않아도 원본 사진 보관이 끝난 뒤 확인해 취소한다.
 *  「알려 주시지 않아도」 꼴은 알려 주는 길을 감추지 않으려고 고른 것이다(같은 절 (5) 아래 설명).
 *  이메일은 `CONTACT_EMAIL`(사업자 정보 이메일 = 환불정책 연락처)에서 조립한다. 조사 `로` 는 주소 끝
 *  `co` 가 받침 없이 읽혀서다. 주소를 바꾸면 조사도 다시 본다.
 *  정산 명세 아래 l1 의 기간 문장은 `public-copy.ts` `REFUND_ARRIVAL_NOTICE` 다(환불정책 제4조와 한 자리). */
export const UNMADE_SHEET_RETURN_NOTICE =
  `다시 만들지 않으실 장은 ${CONTACT_EMAIL}로 알려 주시면 확인한 뒤 취소합니다. ` +
  "알려 주시지 않아도 원본 사진 보관이 끝난 뒤 확인해 취소합니다.";

/** P-02 제2조 2 · A-26 · W-26 · W-26p. **한 문장을 네 자리가 쓴다**(목업 용어 표, 2026-10-03
 *  셋째 회차). 만든 장을 돌려드리지 않는다는 사실이 결제가 끝난 뒤 배너에서 처음 나오면, 결제할
 *  때 셀러가 받아들인 내용이 아니었다고 다투어질 여지가 있다(법무 `6A.14`(2)ⓕ · `L-F52`).
 *  그래서 결제 버튼 앞에 같은 글자로 선다.
 *
 *  `확인 조치 미정` 배지는 웹(`Pay.tsx`)에서 2026-10-04 「변호사 확인 없이 연다」 사용자 결정으로
 *  걷었다. 앱(`pay/confirm.tsx`)에만 남아 있어 앱 회차에 걷는다. 체크 칸은 짓지 않는다
 *  (`L-F52`-1 · `V-47`).
 *
 *  ⛔ **이름을 바꾸지 않는다.** 공개 환불정책 `P-02`(`apps/web/src/screens/public/RefundPolicy.tsx`, #153)가 이 이름으로 import 한다.
 *  ⛔ `만든 장은 결과를 받은 장이라` · `환불 불가` 같은 말로 바꾸지 않는다(목업 용어 표의 금지어). */
export const MADE_SHEET_NO_REFUND_NOTICE =
  "만들기를 마친 장은 그 장의 결제를 취소할 수 없고, 그 금액을 돌려드리지 않습니다.";

/** A-24b · W-24 멈췄을 때 돈 배너 첫 줄. */
export const STALLED_BILLING_NOTICE = "받아 보실 수 있게 된 장만 청구됩니다.";

/** A-24b · W-24 · A-28 이 함께 쓰는 문장. 시점(30일 등)을 말하지 않는 조건문이다. */
export const FAILED_RETURNS_UNLESS_REMADE =
  "못 만든 장은 다시 만들지 않으시면 그 금액이 돌아갑니다.";

/** A-31 · W-31 위쪽 ₩ 배너 첫 문장. amount 가 null 이면(스냅샷을 모름) 금액을 빼고 말한다.
 *  ₩0 을 지어내지 않는다. 조사 `은` 은 formatKrw 결과가 "원"으로 읽혀 받침이 있어서다. */
export function failedImagesAmountNotice(count: number, amount: number | null): string {
  return amount === null
    ? `못 만든 ${count}장 금액은 다시 만들지 않으시면 돌아갑니다.`
    : `못 만든 ${count}장 금액 ${formatKrw(amount)}은 다시 만들지 않으시면 돌아갑니다.`;
}

/** A-23 · W-24 전부 실패 ₩ 배너 첫 줄. amount 가 null 이면 금액을 빼고 말한다. */
export function totalFailureAmountNotice(amount: number | null): string {
  return amount === null
    ? "한 장도 못 만들어 받는 금액이 없습니다. 이미 받은 금액이 있으면 전부 돌아갑니다."
    : `한 장도 못 만들어 받는 금액이 없습니다. 이미 받은 금액이 있으면 ${formatKrw(amount)}이 전부 돌아갑니다.`;
}

/** A-26 · W-26 단가 줄의 이름. 2026-09-14 O-44 로 「부가세 포함」이 확정돼 `부가세 포함 미정`
 *  배지를 걷고 확정된 말을 적는다. 글자는 목업 `A-26`·`W-26` 의 `장당 · 부가세 포함` 그대로다.
 *  부가세 말은 **단가를 적는 줄에만** 둔다(PRD `7.0`). 합계·돌아간 금액에는 붙이지 않는다. */
export const UNIT_PRICE_ROW_LABEL = "장당 · 부가세 포함";

/** 금액 옆에 붙이는 짧은 말. O-44(2026-09-14) 확정. W-01 단가 줄처럼 표가 아닌 자리가 쓴다. */
export const VAT_INCLUDED = "부가세 포함";

/** 금액 바로 아래 배너가 부가세를 말하는 문장. O-44(2026-09-14) 확정 전에는 이 자리에
 *  「부가세가 포함된 값인지는 아직 정해지지 않았습니다」가 있었다. 앱 A-25·A-26 과 웹 W-20 이
 *  같은 문장을 쓴다. 각자 적으면 한쪽만 고쳐진다. */
export const VAT_INCLUDED_NOTICE = "부가세가 포함된 금액입니다.";

/** 결제사에 넘기는 주문명. 결제창과 카드 명세에 그대로 찍힌다.
 *
 * ⛔ **셀러의 상품명을 넣지 않는다.** 넣으면 결제창과 카드 명세에 셀러 상품(예: 「무광 세라믹
 * 머그」)을 우리가 판 것처럼 찍히고, 셀러의 영업 정보가 결제사로 나간다
 * (`docs/LEGAL_IP_GUIDE.md` L-F55·L-F53 축). 우리가 파는 것은 **서비스**다.
 * 그래서 인자를 장수 하나로만 받는다. 상품명을 넘길 자리가 애초에 없다.
 *
 * 상품 이름 `상세페이지 이미지 만들기` 는 목업 결제 심사 페이지와 PRD `7.5.9` 를 따른다. */
export function paymentOrderName(imageCount: number): string {
  return `상세페이지 이미지 만들기 · 사진 ${imageCount}장`;
}
